import { create } from 'zustand'
import type { PlatformAdmin } from '../types'

const SESSION_KEY = 'palstro-admin-session'
const SESSION_TTL = 2 * 60 * 60 * 1000 // 2 hours in ms

export interface AdminSessionData {
  admin: PlatformAdmin
  loginAt: number
  expiresAt: number
}

function loadSession(): AdminSessionData | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as AdminSessionData
    if (Date.now() > data.expiresAt) {
      sessionStorage.removeItem(SESSION_KEY)
      return null
    }
    return data
  } catch {
    return null
  }
}

function saveSession(admin: PlatformAdmin): AdminSessionData {
  const session: AdminSessionData = {
    admin,
    loginAt: Date.now(),
    expiresAt: Date.now() + SESSION_TTL,
  }
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
  return session
}

function clearSession() {
  sessionStorage.removeItem(SESSION_KEY)
}

interface AdminStore {
  admin: PlatformAdmin | null
  isAuthenticated: boolean
  expiresAt: number | null

  login: (admin: PlatformAdmin) => void
  logout: () => void
  checkExpiry: () => boolean // returns true if still valid
  refreshExpiry: () => void
}

const initialSession = loadSession()

export const useAdminStore = create<AdminStore>((set, get) => ({
  admin: initialSession?.admin ?? null,
  isAuthenticated: initialSession != null,
  expiresAt: initialSession?.expiresAt ?? null,

  login: (admin) => {
    const session = saveSession(admin)
    set({ admin, isAuthenticated: true, expiresAt: session.expiresAt })
  },

  logout: () => {
    clearSession()
    set({ admin: null, isAuthenticated: false, expiresAt: null })
  },

  checkExpiry: () => {
    const { expiresAt, logout } = get()
    if (!expiresAt || Date.now() > expiresAt) {
      logout()
      return false
    }
    return true
  },

  refreshExpiry: () => {
    const { admin } = get()
    if (!admin) return
    const session = saveSession(admin)
    set({ expiresAt: session.expiresAt })
  },
}))
